import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usersApi, localiApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Users,
  Shield,
  UserCog,
  BarChart3,
  Loader2,
  UserPlus,
  Mail,
  Lock,
  User,
  Eye,
  EyeOff,
  Building2,
  ShieldCheck,
  Copy,
  Settings2,
  KeyRound,
  Trash2,
  AlertTriangle,
} from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { cn } from '../lib/utils';

const RUOLI = [
  { value: 'admin', label: 'Admin', icon: Shield, color: 'bg-red-100 text-red-800 border-red-200' },
  { value: 'manager', label: 'Manager', icon: UserCog, color: 'bg-blue-100 text-blue-800 border-blue-200' },
  { value: 'operatore_cassa', label: 'Operatore Cassa', icon: Users, color: 'bg-green-100 text-green-800 border-green-200' },
  { value: 'analista', label: 'Analista', icon: BarChart3, color: 'bg-purple-100 text-purple-800 border-purple-200' },
];

const getRoleInfo = (ruolo) => RUOLI.find((r) => r.value === ruolo) || RUOLI[2];

const GestioneUtentiPage = () => {
  const navigate = useNavigate();
  const { user, canManageUsers } = useAuth();
  const [users, setUsers] = useState([]);
  const [locali, setLocali] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create user dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [newUser, setNewUser] = useState({
    nome: '',
    email: '',
    password: '',
    ruolo: 'operatore_cassa',
    assegnazioni: [], // [{ venue_id, ruolo }]
  });
  const [generatedPassword, setGeneratedPassword] = useState(null); // post-creazione

  // Memberships dialog
  const [showMembershipsDialog, setShowMembershipsDialog] = useState(false);
  const [membershipsTarget, setMembershipsTarget] = useState(null);
  const [membershipsDraft, setMembershipsDraft] = useState([]);
  const [savingMemberships, setSavingMemberships] = useState(false);

  // Delete user dialog
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Reset password dialog
  const [resetTarget, setResetTarget] = useState(null);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    if (!canManageUsers()) {
      navigate('/impostazioni', { replace: true });
      return;
    }
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAll = async () => {
    try {
      const [usersData, localiData] = await Promise.all([
        usersApi.getAll(),
        localiApi.getAll(true),
      ]);
      setUsers(usersData);
      setLocali(localiData);
    } catch (e) {
      toast.error('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  const resetCreateForm = () => {
    setNewUser({ nome: '', email: '', password: '', ruolo: 'operatore_cassa', assegnazioni: [] });
    setShowPassword(false);
  };

  const toggleAssegnazione = (venueId, ruolo = 'operatore_cassa') => {
    setNewUser((prev) => {
      const exists = prev.assegnazioni.find((a) => a.venue_id === venueId);
      if (exists) {
        return { ...prev, assegnazioni: prev.assegnazioni.filter((a) => a.venue_id !== venueId) };
      }
      return { ...prev, assegnazioni: [...prev.assegnazioni, { venue_id: venueId, ruolo }] };
    });
  };

  const setAssegnazioneRuolo = (venueId, ruolo) => {
    setNewUser((prev) => ({
      ...prev,
      assegnazioni: prev.assegnazioni.map((a) => (a.venue_id === venueId ? { ...a, ruolo } : a)),
    }));
  };

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.nome) {
      toast.error('Compila nome ed email');
      return;
    }
    if (newUser.password && newUser.password.length < 6) {
      toast.error('La password deve essere di almeno 6 caratteri');
      return;
    }
    setCreating(true);
    try {
      const payload = {
        email: newUser.email,
        nome: newUser.nome,
        ruolo: newUser.ruolo,
        ...(newUser.password ? { password: newUser.password } : {}),
        locali: newUser.assegnazioni,
      };
      const created = await usersApi.create(payload);
      toast.success(`Utente "${created.nome}" creato con successo`);
      // Mostra la password iniziale (random o quella scelta) per copy una sola volta
      if (created.initial_password) {
        setGeneratedPassword({
          email: created.email,
          password: created.initial_password,
        });
      } else {
        setShowCreateDialog(false);
        resetCreateForm();
      }
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione utente');
    } finally {
      setCreating(false);
    }
  };

  const handleCopyPassword = () => {
    if (generatedPassword?.password) {
      navigator.clipboard.writeText(generatedPassword.password);
      toast.success('Password copiata');
    }
  };

  const closeGeneratedDialog = () => {
    setGeneratedPassword(null);
    setShowCreateDialog(false);
    resetCreateForm();
  };

  const openMembershipsDialog = (target) => {
    setMembershipsTarget(target);
    setMembershipsDraft(
      (target.memberships || []).map((m) => ({ venue_id: m.venue_id, ruolo: m.ruolo }))
    );
    setShowMembershipsDialog(true);
  };

  const toggleDraftMembership = (venueId, ruolo = 'operatore_cassa') => {
    setMembershipsDraft((prev) => {
      const exists = prev.find((a) => a.venue_id === venueId);
      if (exists) {
        return prev.filter((a) => a.venue_id !== venueId);
      }
      return [...prev, { venue_id: venueId, ruolo }];
    });
  };

  const setDraftRuolo = (venueId, ruolo) => {
    setMembershipsDraft((prev) =>
      prev.map((a) => (a.venue_id === venueId ? { ...a, ruolo } : a))
    );
  };

  const handleSaveMemberships = async () => {
    if (!membershipsTarget) return;
    setSavingMemberships(true);
    try {
      await usersApi.updateMemberships(membershipsTarget.id, membershipsDraft);
      toast.success('Assegnazioni aggiornate');
      setShowMembershipsDialog(false);
      setMembershipsTarget(null);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSavingMemberships(false);
    }
  };

  const handleUpdateActive = async (userId, attivo) => {
    try {
      await usersApi.update(userId, { attivo });
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, attivo } : u)));
      toast.success(attivo ? 'Utente riattivato' : 'Utente disattivato');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'aggiornamento");
    }
  };

  const handleResetPassword = async () => {
    if (!resetTarget) return;
    setResetting(true);
    try {
      const updated = await usersApi.resetPassword(resetTarget.id);
      setResetTarget(null);
      if (updated.initial_password) {
        setGeneratedPassword({ email: updated.email, password: updated.initial_password });
      }
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel reset della password');
    } finally {
      setResetting(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteTarget) return;
    if (deleteConfirmText.trim() !== deleteTarget.email) {
      toast.error("L'email digitata non corrisponde");
      return;
    }
    setDeleting(true);
    try {
      await usersApi.remove(deleteTarget.id);
      toast.success(`Utente ${deleteTarget.nome} eliminato`);
      setDeleteTarget(null);
      setDeleteConfirmText('');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nella cancellazione');
    } finally {
      setDeleting(false);
    }
  };

  const localiAttivi = locali.filter((l) => l.stato === 'attivo');

  return (
    <div className="min-h-screen pb-24">
      <header className="glass-header sticky top-0 z-40 px-4 py-4 border-b border-border">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/impostazioni')} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-heading tracking-tight">Gestione Utenti</h1>
              <p className="text-xs text-muted-foreground">{users.length} utenti totali</p>
            </div>
          </div>

          <Dialog open={showCreateDialog} onOpenChange={(o) => { setShowCreateDialog(o); if (!o) resetCreateForm(); }}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="create-user-btn"><UserPlus className="w-4 h-4 mr-2" /> Nuovo Utente</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-primary" /> Crea nuovo utente
                </DialogTitle>
                <DialogDescription>
                  Inserisci i dati. Se non imposti una password, ne verrà generata una automaticamente — mostrata <strong>una sola volta</strong>.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><User className="w-4 h-4" /> Nome e Cognome *</Label>
                  <Input
                    data-testid="new-user-nome"
                    placeholder="Mario Rossi"
                    value={newUser.nome}
                    onChange={(e) => setNewUser({ ...newUser, nome: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Mail className="w-4 h-4" /> Email *</Label>
                  <Input
                    data-testid="new-user-email"
                    type="email"
                    placeholder="mario.rossi@spaziomarconi.it"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Lock className="w-4 h-4" /> Password iniziale (opzionale)</Label>
                  <div className="relative">
                    <Input
                      data-testid="new-user-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Lascia vuota per generarla automaticamente"
                      value={newUser.password}
                      onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full"
                      onClick={() => setShowPassword((v) => !v)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">L'utente sarà forzato a cambiarla al primo accesso.</p>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Shield className="w-4 h-4" /> Ruolo predefinito *</Label>
                  <Select value={newUser.ruolo} onValueChange={(value) => setNewUser({ ...newUser, ruolo: value })}>
                    <SelectTrigger data-testid="new-user-ruolo"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {RUOLI.map((role) => (
                        <SelectItem key={role.value} value={role.value}>
                          <div className="flex items-center gap-2">
                            <role.icon className="w-4 h-4" />
                            <span>{role.label}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Building2 className="w-4 h-4" /> Assegna a locali</Label>
                  {localiAttivi.length === 0 ? (
                    <p className="text-sm text-muted-foreground italic">Nessun locale attivo. Crea prima un locale.</p>
                  ) : (
                    <div className="space-y-2">
                      {localiAttivi.map((l) => {
                        const ass = newUser.assegnazioni.find((a) => a.venue_id === l.id);
                        const checked = !!ass;
                        return (
                          <div
                            key={l.id}
                            className={cn(
                              'flex items-center justify-between gap-2 p-2 rounded-md border',
                              checked ? 'border-primary bg-primary/5' : 'border-border'
                            )}
                          >
                            <button
                              type="button"
                              onClick={() => toggleAssegnazione(l.id, newUser.ruolo)}
                              data-testid={`assign-venue-${l.id}`}
                              className="flex items-center gap-2 text-left flex-1 min-w-0"
                            >
                              <span className={cn(
                                'inline-flex w-4 h-4 rounded border items-center justify-center text-[10px] font-bold shrink-0',
                                checked ? 'bg-primary text-primary-foreground border-primary' : 'border-muted-foreground/40'
                              )}>
                                {checked ? '✓' : ''}
                              </span>
                              <span className="truncate text-sm">{l.nome}</span>
                            </button>
                            {checked && (
                              <Select value={ass.ruolo} onValueChange={(v) => setAssegnazioneRuolo(l.id, v)}>
                                <SelectTrigger className="h-8 w-36 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {RUOLI.map((r) => (
                                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Annulla</Button>
                <Button onClick={handleCreateUser} disabled={creating} data-testid="confirm-create-btn">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
                  Crea utente
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <main className="px-4 py-6 max-w-2xl mx-auto space-y-3">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : users.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <Users className="w-16 h-16 text-muted-foreground/50 mx-auto mb-4" />
              <p className="text-muted-foreground">Nessun utente nel sistema</p>
            </CardContent>
          </Card>
        ) : (
          users.map((u) => {
            const roleInfo = getRoleInfo(u.ruolo);
            const RoleIcon = roleInfo.icon;
            const isCurrentUser = u.id === user?.id;
            return (
              <Card key={u.id} data-testid={`user-card-${u.id}`} className={cn('transition-all', !u.attivo && 'opacity-60 bg-muted/50')}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={cn('w-10 h-10 rounded-full flex items-center justify-center shrink-0', u.attivo ? 'bg-primary/10' : 'bg-muted')}>
                        <RoleIcon className={cn('w-5 h-5', u.attivo ? 'text-primary' : 'text-muted-foreground')} />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold truncate">{u.nome}</p>
                          {isCurrentUser && <Badge variant="outline" className="text-xs">Tu</Badge>}
                          {u.is_super_admin && (
                            <Badge className="text-xs bg-amber-100 text-amber-800 border-amber-300">
                              <ShieldCheck className="w-3 h-3 mr-1" /> Super Admin
                            </Badge>
                          )}
                          {!u.attivo && <Badge variant="secondary" className="text-xs">Disattivato</Badge>}
                          {u.must_change_password && (
                            <Badge className="text-xs bg-orange-100 text-orange-800 border-orange-200">Pwd da cambiare</Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground truncate">{u.email}</p>
                        <p className="text-xs text-muted-foreground">
                          Creato {format(new Date(u.created_at), 'd MMM yyyy', { locale: it })}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <Switch
                        checked={u.attivo}
                        onCheckedChange={(c) => handleUpdateActive(u.id, c)}
                        disabled={isCurrentUser}
                        data-testid={`active-switch-${u.id}`}
                      />
                    </div>
                  </div>

                  {/* Memberships */}
                  <div className="mt-3 pt-3 border-t border-border">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                        <Building2 className="w-3 h-3" /> Assegnato a {u.memberships?.length || 0} locale{(u.memberships?.length || 0) === 1 ? '' : 'i'}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={() => openMembershipsDialog(u)}
                        data-testid={`manage-memberships-${u.id}`}
                      >
                        <Settings2 className="w-3 h-3 mr-1" /> Gestisci
                      </Button>
                    </div>
                    {u.memberships?.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {u.memberships.map((m) => {
                          const info = getRoleInfo(m.ruolo);
                          return (
                            <span
                              key={m.venue_id}
                              className={cn('inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border', info.color)}
                            >
                              <info.icon className="w-3 h-3" />
                              <span className="font-medium">{m.venue_nome}</span>
                              <span className="opacity-70">·</span>
                              <span>{info.label}</span>
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground italic">Nessun locale assegnato</p>
                    )}
                  </div>

                  {/* Azioni sicurezza */}
                  {!isCurrentUser && (
                    <div className="mt-3 pt-3 border-t border-border flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={() => setResetTarget(u)}
                        data-testid={`reset-password-${u.id}`}
                      >
                        <KeyRound className="w-3 h-3 mr-1" /> Reset password
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
                        onClick={() => { setDeleteTarget(u); setDeleteConfirmText(''); }}
                        data-testid={`delete-user-${u.id}`}
                      >
                        <Trash2 className="w-3 h-3 mr-1" /> Elimina
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </main>

      {/* Dialog: password generata mostrata una volta */}
      <Dialog open={!!generatedPassword} onOpenChange={(o) => { if (!o) closeGeneratedDialog(); }}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-primary" /> Password iniziale generata
            </DialogTitle>
            <DialogDescription>
              Comunica questa password all'utente: <strong>{generatedPassword?.email}</strong>. Non sarà più visibile dopo questa schermata. L'utente sarà obbligato a sostituirla al primo accesso.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-muted p-3 rounded-md font-mono text-sm break-all">
            {generatedPassword?.password}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleCopyPassword}><Copy className="w-4 h-4 mr-2" /> Copia</Button>
            <Button onClick={closeGeneratedDialog}>Ho preso nota</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: gestione assegnazioni esistente */}
      <Dialog open={showMembershipsDialog} onOpenChange={setShowMembershipsDialog}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary" /> Assegnazioni di {membershipsTarget?.nome}
            </DialogTitle>
            <DialogDescription>
              Spunta i locali a cui assegnare l'utente. Imposta un ruolo indipendente per ognuno.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {localiAttivi.map((l) => {
              const ass = membershipsDraft.find((a) => a.venue_id === l.id);
              const checked = !!ass;
              return (
                <div
                  key={l.id}
                  className={cn(
                    'flex items-center justify-between gap-2 p-2 rounded-md border',
                    checked ? 'border-primary bg-primary/5' : 'border-border'
                  )}
                >
                  <button
                    type="button"
                    onClick={() => toggleDraftMembership(l.id, membershipsTarget?.ruolo || 'operatore_cassa')}
                    data-testid={`draft-toggle-${l.id}`}
                    className="flex items-center gap-2 text-left flex-1 min-w-0"
                  >
                    <span className={cn(
                      'inline-flex w-4 h-4 rounded border items-center justify-center text-[10px] font-bold shrink-0',
                      checked ? 'bg-primary text-primary-foreground border-primary' : 'border-muted-foreground/40'
                    )}>
                      {checked ? '✓' : ''}
                    </span>
                    <span className="truncate text-sm">{l.nome}</span>
                  </button>
                  {checked && (
                    <Select value={ass.ruolo} onValueChange={(v) => setDraftRuolo(l.id, v)}>
                      <SelectTrigger className="h-8 w-36 text-xs" data-testid={`draft-role-${l.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {RUOLI.map((r) => (
                          <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              );
            })}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowMembershipsDialog(false)}>Annulla</Button>
            <Button onClick={handleSaveMemberships} disabled={savingMemberships} data-testid="save-memberships-btn">
              {savingMemberships ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Settings2 className="w-4 h-4 mr-2" />}
              Salva assegnazioni
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: conferma reset password */}
      <Dialog open={!!resetTarget} onOpenChange={(o) => { if (!o) setResetTarget(null); }}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-primary" /> Reset password
            </DialogTitle>
            <DialogDescription>
              Verrà generata una <strong>nuova password casuale</strong> per{' '}
              <strong>{resetTarget?.nome}</strong> ({resetTarget?.email}). La password attuale
              smetterà di funzionare immediatamente. L'utente dovrà cambiarla al primo accesso.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-sm">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <p>La nuova password sarà mostrata <strong>una sola volta</strong>. Annotala prima di chiudere la finestra.</p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setResetTarget(null)}>Annulla</Button>
            <Button onClick={handleResetPassword} disabled={resetting} data-testid="confirm-reset-password-btn">
              {resetting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <KeyRound className="w-4 h-4 mr-2" />}
              Genera nuova password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: conferma eliminazione utente */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) { setDeleteTarget(null); setDeleteConfirmText(''); } }}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="w-5 h-5" /> Elimina utente
            </DialogTitle>
            <DialogDescription>
              Stai per eliminare definitivamente <strong>{deleteTarget?.nome}</strong> ({deleteTarget?.email})
              e tutte le sue assegnazioni ai locali. I dati operativi creati da questo utente (chiusure, uscite)
              <strong> non saranno cancellati</strong> e resteranno nei rispettivi locali.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-start gap-2 p-3 rounded-md bg-red-50 border border-red-200 text-red-900 text-sm">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <p>Operazione <strong>irreversibile</strong>. Per confermare, digita l'email dell'utente qui sotto.</p>
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Email di conferma</Label>
            <Input
              data-testid="delete-confirm-input"
              placeholder={deleteTarget?.email}
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              autoComplete="off"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setDeleteTarget(null); setDeleteConfirmText(''); }}>Annulla</Button>
            <Button
              variant="destructive"
              onClick={handleDeleteUser}
              disabled={deleting || deleteConfirmText.trim() !== deleteTarget?.email}
              data-testid="confirm-delete-user-btn"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Elimina definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GestioneUtentiPage;
